// src/mobile_pages/const/OrderComplete_view.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  Printer,
  Trash2,
  Edit2,
  FileText,
  User,
  Calendar,
  CheckCircle2,
  Paperclip,
  Activity,
  Link as LinkIcon,
  ClipboardList
} from "lucide-react";

import { getCompletionReportDetail } from "../../api/const";
import { viewApprovalLine } from "../../api/approval";
import { softDeleteDoc } from "../../api/papers";
import { getProjectName } from "../../api/project";

// 기존 컴포넌트 재사용
import TinyMCEpreview from "../../components/const/TinyMCEpreview";
import ApprovalAction from "../../components/approval/ApprovalAction";
import ApprovalLine_view from "../../components/approval/ApprovalLine_view";
import FileList from "../../components/common/papers/FileList";
import MobilePhotoBoardReadonly from "./components/MobilePhotoBoardReadonly";

export default function OrderCompleteView() {
  const { completionreportId: dns, id: projectId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [approvalLines, setApprovalLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectName, setProjectName] = useState("");

  // 섹션 확장 상태
  const [expandedSections, setExpandedSections] = useState({
    info: true,
    approval: true,
    link: true,
    content: true,
    attachment: true,
    photoboard: true,
  });

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. 프로젝트 이름
        const projRes = await getProjectName(projectId);
        setProjectName(projRes.name);

        // 2. 완료보고서 데이터
        const docRes = await getCompletionReportDetail(dns);
        // API 응답 구조가 때로는 { data: ... } 또는 바로 ... 로 옴
        const docData = docRes.data || docRes;
        setData(docData);

        // 3. 결재선
        try {
          const appRes = await viewApprovalLine(dns);
          setApprovalLines(appRes.data.approval_lines || []);
        } catch (e) {
          console.log("결재선 없음 or 오류", e);
        }

      } catch (err) {
        console.error("데이터 로딩 실패:", err);
        alert("데이터를 불러오지 못했습니다.");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dns, projectId, navigate]);

  // 권한 체크
  const isOwner = data?.created_by?.id === currentUser?.id;
  const canEdit = isOwner && (!data?.status || data?.status === "submit");

  // 삭제 핸들러
  const handleDelete = async () => {
    if (!window.confirm("정말 이 문서를 삭제하시겠습니까? (삭제 후 복구 불가)")) return;
    try {
      await softDeleteDoc(dns, true);
      alert("삭제되었습니다.");
      navigate(-1);
    } catch (e) {
      console.error(e);
      alert("삭제 실패");
    }
  };

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("ko-KR");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500 text-sm">로딩 중...</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-24">
        {/* 헤더 */}
        <header className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-gray-500">시공관리 · 완료보고서</p>
            <div className="flex items-center gap-1.5">
              {/* data.status 또는 data.approval?.is_approved 확인 필요 */}
              {data.approval?.is_approved === 'approved' ? (
                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold border border-green-200">승인됨</span>
              ) : data.approval?.is_approved === 'rejected' ? (
                <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold border border-red-200">반려됨</span>
              ) : (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold border border-amber-200">진행중</span>
              )}
            </div>
          </div>
          <h1 className="text-xl font-bold text-slate-900 leading-tight">
            {data.title || "제목 없음"}
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            DNS: {data.dns || dns}
          </p>
        </header>

        <div className="space-y-3">
          {/* 1. 기본 정보 */}
          <Section
            title="기본 정보"
            expanded={expandedSections.info}
            onToggle={() => toggleSection("info")}
            icon={<FileText size={16} className="text-blue-500" />}
          >
            <div className="space-y-4">
              {/* 현장명 */}
              <InfoCard
                label="현장명"
                value={data.project_name || projectName || "-"}
                icon={<Activity size={12} />}
                fullWidth
              />

              <div className="grid grid-cols-2 gap-3">
                <InfoCard
                  label="작성자"
                  value={data.created_by?.name || "-"}
                  icon={<User size={12} />}
                />
                <InfoCard
                  label="작성일"
                  value={formatDate(data.created_at)}
                  icon={<Calendar size={12} />}
                />
              </div>

              <InfoCard
                label="검토자(승인)"
                value={data.approval?.approver_name || "-"}
                icon={<CheckCircle2 size={12} />}
                fullWidth
              />
            </div>
          </Section>

          {/* 2. 결재 현황 */}
          <Section
            title="결재 현황"
            expanded={expandedSections.approval}
            onToggle={() => toggleSection("approval")}
            icon={<CheckCircle2 size={16} className="text-purple-500" />}
          >
            <div className="space-y-4">
              <div className="overflow-x-auto pb-2">
                <ApprovalLine_view lines={approvalLines} />
              </div>
              <ApprovalAction approvalLines={approvalLines} />
            </div>
          </Section>

          {/* 3. 연결 정보 (작업지시서) */}
          <Section
            title="연결 정보"
            expanded={expandedSections.link}
            onToggle={() => toggleSection("link")}
            icon={<LinkIcon size={16} className="text-indigo-500" />}
          >
            {data.meta?.workorder ? (
              <div
                onClick={() => navigate(`/project/${projectId}/const/order/${data.meta.workorder}/view`)}
                className="flex items-center justify-between p-3 rounded-xl border border-indigo-100 bg-indigo-50/50 active:scale-[0.98] transition-transform cursor-pointer"
              >
                <div className="flex items-start gap-3 overflow-hidden">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <ClipboardList size={20} className="text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-indigo-500 mb-0.5">작업지시서 연결됨</p>
                    <p className="text-sm font-semibold text-slate-800 truncate leading-tight">
                      {data.workorder_title || data.meta.workorder_title || "작업지시서 보기"}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{data.meta.workorder}</p>
                  </div>
                </div>
                <ChevronLeft size={18} className="rotate-180 text-indigo-300" />
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-2">연결된 작업지시서가 없습니다.</p>
            )}
          </Section>

          {/* 4. 상세 내용 */}
          <Section
            title="상세 내용"
            expanded={expandedSections.content}
            onToggle={() => toggleSection("content")}
            icon={<FileText size={16} className="text-green-500" />}
          >
            <div className="min-h-[100px] text-sm text-slate-700">
              <TinyMCEpreview html={data.meta.detail || "<p>내용이 없습니다.</p>"} />
            </div>
          </Section>

          {/* 5. 사진 대지 */}
          <Section
            title="사진 대지"
            expanded={expandedSections.photoboard}
            onToggle={() => toggleSection("photoboard")}
            icon={<Paperclip size={16} className="text-orange-500" />}
          >
            {data.meta?.photoboard ? (
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <MobilePhotoBoardReadonly photoboardId={data.meta.photoboard} />
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-2">연결된 사진대지가 없습니다.</p>
            )}
          </Section>

          {/* 6. 첨부 자료 */}
          <Section
            title="첨부 자료"
            expanded={expandedSections.attachment}
            onToggle={() => toggleSection("attachment")}
            icon={<Paperclip size={16} className="text-gray-500" />}
          >
            <FileList dns={dns} />
          </Section>
        </div>
      </main>

      {/* 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <div className="flex items-center gap-2">
          {/* 목록 버튼 (항상 보임) */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all flex items-center justify-center gap-1"
          >
            <ChevronLeft size={16} />
            목록
          </button>

          {/* 인쇄 버튼 */}
          <button
            type="button"
            onClick={() => window.print()}
            className="w-11 h-11 flex-shrink-0 rounded-full border border-gray-300 bg-white text-gray-700 flex items-center justify-center hover:bg-gray-50 active:scale-[0.97]"
          >
            <Printer size={18} />
          </button>

          {/* 수정/삭제 (권한 있을 때만) */}
          {canEdit && (
            <>
              <button
                type="button"
                onClick={handleDelete}
                className="w-11 h-11 flex-shrink-0 rounded-full border border-red-200 bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 active:scale-[0.97]"
              >
                <Trash2 size={18} />
              </button>
              <button
                type="button"
                onClick={() => {
                  const editUrl = window.location.pathname.replace(/\/view$/, "");
                  navigate(editUrl);
                }}
                className="flex-1 h-11 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 active:scale-[0.97] transition-all flex items-center justify-center gap-1"
              >
                <Edit2 size={16} />
                수정
              </button>
            </>
          )}
        </div>
      </footer>
    </div>
  );
}

// UI 컴포넌트들
function Section({ title, expanded, onToggle, icon, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""
            }`}
        />
      </button>
      {expanded && <div className="p-4">{children}</div>}
    </section>
  );
}

function InfoCard({ label, value, icon, fullWidth = false }) {
  return (
    <div className={`bg-slate-50 rounded-xl p-3 border border-slate-100 ${fullWidth ? 'col-span-2' : ''}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-slate-400">{icon}</span>
        <span className="text-[10px] text-slate-400 font-medium">{label}</span>
      </div>
      <p className="text-[13px] font-semibold text-slate-700 truncate">
        {value}
      </p>
    </div>
  );
}